import { Text, View } from "react-native";
import type { ReactNode } from "react";
import { Archive } from "lucide-react-native";
import { SectionTitle } from "@louise/moon-ui";
import { EMBER, GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import type { JourneyDefinition, PlanAction } from "../plan.types";
import type { StepProgress } from "../hooks/usePlanProgress";
import type { CostEstimate } from "../intelligence/useCostEstimate";
import {
  EggFreezingArchivedBiologyCarouselVariants,
  EggFreezingBiologyDeepCarouselCard,
  EggFreezingBiologyInlineStepSectionCard,
  EggFreezingBiologyStepSectionCard,
} from "../components/EggFreezingBiologyDeepCarouselCard";
import { EggFreezingBiologyCarouselCard } from "../components/EggFreezingBiologyCarouselCard";
import {
  EggFreezingKnowYourNumbersCard,
  EggFreezingKnowYourNumbersChecklistCard,
} from "../components/EggFreezingKnowYourNumbersCard";
import OptimizationCard from "../components/OptimizationCard";
import OptimizationCardV2 from "../components/OptimizationCardV2";
import ConditionInsightCard from "../components/ConditionInsightCard";
import CostEstimateCard from "../components/CostEstimateCard";
import {
  MOCK_CONDITION_CONTENT,
  MOCK_OPTIMIZATION_WINDOW,
} from "./devCardPreview";

type Props = {
  userAge?: number;
  journey: JourneyDefinition;
  progress: StepProgress[];
  costEstimate: CostEstimate | null;
  onAction: (action: PlanAction) => void;
  onOpenSupplement: (name: string) => void;
};

function ArchiveSection({
  title,
  children,
  description,
}: {
  title: string;
  children: ReactNode;
  description?: string;
}) {
  return (
    <View style={{ marginTop: 24 }}>
      <View style={{ marginBottom: 10, paddingHorizontal: 2 }}>
        <SectionTitle>{title}</SectionTitle>
        {description ? (
          <Text
            className="font-dm-sans"
            style={{
              marginTop: 4,
              fontSize: 12,
              lineHeight: 18,
              color: EMBER.colors.textMid,
              fontWeight: "400",
            }}
          >
            {description}
          </Text>
        ) : null}
      </View>
      <View style={{ gap: 12 }}>{children}</View>
    </View>
  );
}

export function HomepageComponentArchive({
  userAge,
  journey,
  progress,
  costEstimate,
  onAction,
  onOpenSupplement,
}: Props) {
  return (
    <>
      <View
        style={{
          ...(GLASS_CARD_STYLE as Record<string, unknown>),
          marginTop: 16,
          padding: 16,
        }}
      >
        <View style={{ flexDirection: "row", alignItems: "center", gap: 9 }}>
          <Archive size={16} color={EMBER.colors.textLight} strokeWidth={1.8} />
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 15,
              lineHeight: 20,
              color: EMBER.colors.text,
              fontWeight: "700",
            }}
          >
            Homepage component archive
          </Text>
        </View>
        <Text
          className="font-dm-sans"
          style={{
            marginTop: 6,
            fontSize: 12,
            lineHeight: 18,
            color: EMBER.colors.textMid,
            fontWeight: "400",
          }}
        >
          Dev-only parking lot for removed or comparison homepage components.
          Use this page to review old copy and interaction patterns without
          keeping them on active user homepages.
        </Text>
      </View>

      <ArchiveSection
        title="Egg-freezing education iterations"
        description="Earlier concepts for the biology carousel and step section framing."
      >
        <EggFreezingArchivedBiologyCarouselVariants
          userAge={userAge}
          onAction={onAction}
        />
        <EggFreezingBiologyCarouselCard />
        <EggFreezingBiologyDeepCarouselCard
          userAge={userAge}
          onAction={onAction}
        />
        <EggFreezingBiologyInlineStepSectionCard
          userAge={userAge}
          onAction={onAction}
        />
        <EggFreezingBiologyStepSectionCard
          userAge={userAge}
          onAction={onAction}
        />
        <EggFreezingKnowYourNumbersChecklistCard onAction={onAction} />
        <EggFreezingKnowYourNumbersCard userAge={userAge} onAction={onAction} />
      </ArchiveSection>

      <ArchiveSection
        title="Legacy support cards"
        description="Useful copy should be harvested into the new lifecycle modules before these are deleted."
      >
        <OptimizationCard
          optimization={MOCK_OPTIMIZATION_WINDOW}
          userAge={userAge}
          onAction={onAction}
        />
        <OptimizationCardV2
          optimization={MOCK_OPTIMIZATION_WINDOW}
          userAge={userAge}
        />
        <ConditionInsightCard
          content={MOCK_CONDITION_CONTENT}
          onPressSupplement={onOpenSupplement}
        />
        {costEstimate ? (
          <CostEstimateCard
            estimate={costEstimate}
            onUploadCoverage={() =>
              onAction({ label: "Upload coverage", screen: "CoverageFlow" })
            }
          />
        ) : null}
      </ArchiveSection>
    </>
  );
}
